import React from "react";
import classNames from "classnames";
import Spinner from "@/components/ui/Spinner/Spinner";
import { resolveLinkTarget } from "@/components/ui/OpticksLink/resolveLinkTarget";
import type { OpticksButtonSize } from "@/components/ui/OpticksButton/OpticksButton";
import "@/components/ui/OpticksButton/OpticksButton.css";

export interface Props {
  variation?: string;
  size?: OpticksButtonSize;
  display?: string;
  shape?: "default" | "rounded";
  disabled?: boolean;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement> | (() => void);
  label?: string | React.ReactNode;
  icon?: React.ReactNode;
  iconTrailing?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
  /** Internal route — renders a react-router `<Link>` styled as a button (SPA nav). */
  to?: string;
  /** External/email destination — renders an `<a>` styled as a button. A non-mailto
   *  href auto-opens a new tab (target/rel) + external icon; mailto adds the mail icon. */
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
}

// Standalone twin of OpticksButton that renders a link (or a disabled <button>)
// wearing the same `.opticks-button` styles. The visual code is intentionally a
// copy of OpticksButton's — kept separate rather than shared so each component
// stays simple and self-contained.
const OpticksButtonLink = ({
  variation,
  display,
  shape,
  className,
  loading = false,
  size,
  disabled,
  label,
  icon,
  iconTrailing,
  onClick,
  "aria-label": ariaLabel,
  to,
  href,
  target,
  rel,
}: Props) => {
  const isIconOnly = !!icon && !label && !iconTrailing;
  const resolvedVariation = variation ?? (isIconOnly ? "ghost" : "primary");
  const resolvedSize = size ?? (isIconOnly ? "unset" : "m");
  const resolvedShape = resolvedVariation === "soft" ? shape : undefined;
  const isDarkBackground = ["primary", "highlighted", "destructive"].includes(
    resolvedVariation
  );

  const classes = classNames(
    "opticks-button",
    { [`variation-${resolvedVariation}`]: resolvedVariation },
    { [`size-${resolvedSize}`]: resolvedSize },
    { [`display-${display}`]: display },
    { [`shape-${resolvedShape}`]: resolvedShape },
    { "is-loading": loading },
    { [`${className}`]: className && className.length > 0 }
  );

  const { Element, targetProps, leadingIcon, trailingIcon } = resolveLinkTarget({
    to,
    href,
    target,
    rel,
  });

  const content = (
    <>
      {loading && (
        <Spinner size={16} color={isDarkBackground ? "mono" : "blue"} />
      )}
      {icon ?? leadingIcon}
      {label && <b>{label}</b>}
      {iconTrailing ?? trailingIcon}
    </>
  );

  // Anchors can't be natively disabled — render a real <button> when
  // disabled/loading (no navigation, so onClick is intentionally dropped).
  if (disabled || loading) {
    return (
      <button
        className={classes}
        type="button"
        disabled
        aria-label={ariaLabel}
      >
        {content}
      </button>
    );
  }

  return (
    <Element
      className={classes}
      {...targetProps}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {content}
    </Element>
  );
};

export default React.memo(OpticksButtonLink);
